import { MAX_EFFECT_TICKS, RefusalReason, TICK_MS } from "odyssey-shared";
import { say } from "../../words.ts";
import { pageKeyHints } from "../touch/key-hints.ts";

/**
 * The ability bar's, the cast bar's and the status rows' wording (P2.2-T4; ADR-061's copy rules). The words are the
 * language files' (`bar.*` in `odyssey-lang`). The times are tables built once at load, so the per-frame ticker
 * (`ticker.ts`) only ever assigns a string it already holds.
 */

/**
 * A slot's accessible name: its ability's or item's name, or "Empty slot", the number carried for the item slot, and
 * its key.
 *
 * @param name The ability's or item's name, or null for an empty slot.
 * @param key The slot's key.
 * @param count The number carried, for the item slot; null for any other.
 * @param keyed Whether a key may be named (`touch/key-hints.ts`); without one the name carries no key.
 * @returns The name.
 * @example
 * slotLabel("Strike", "1");
 * // "Strike, key 1"
 * slotLabel("Minor Healing Potion", "0", 3);
 * // "Minor Healing Potion, 3, key 0"
 */
export function slotLabel(name: string | null, key: string, count: number | null = null, keyed: boolean = pageKeyHints.getSnapshot()): string {
  const named = name ?? say("bar.empty");
  if (!keyed) {
    return count === null ? say("bar.slotTouch", { name: named }) : say("bar.slotCountedTouch", { name: named, count: String(count) });
  }
  return count === null ? say("bar.slot", { name: named, key }) : say("bar.slotCounted", { name: named, count: String(count), key });
}

/**
 * The swap control's accessible name on a paged bar (P1.9-T133; the design's touch swap button, "Swap ability page: 1
 * of 2, slots 1 to 5"), with its key, as a slot's name says its key.
 *
 * @param page The page shown, from 0.
 * @param pages How many pages the bar has.
 * @param slots The slots the page shows, by index from 0, in order.
 * @param key The swap's key as the viewer reads it.
 * @param keyed Whether a key may be named; without one the name carries no key.
 * @returns The name.
 * @example
 * swapLabel(0, 2, [0, 1, 2, 3, 4], "`");
 * // "Swap ability page: 1 of 2, slots 1 to 5, key `"
 */
export function swapLabel(page: number, pages: number, slots: readonly number[], key: string, keyed: boolean = pageKeyHints.getSnapshot()): string {
  if (!keyed) {
    return say("bar.swapTouch", { page: String(page + 1), pages: String(pages), first: String((slots[0] ?? 0) + 1), last: String((slots[slots.length - 1] ?? 0) + 1) });
  }
  return say("bar.swap", { page: String(page + 1), pages: String(pages), first: String((slots[0] ?? 0) + 1), last: String((slots[slots.length - 1] ?? 0) + 1), key });
}

/**
 * The cast bar's short reason for an act the server refused (ADR-074 decision 3's reasons), beside the message line's
 * sentence (`fight/wording.ts`).
 *
 * @param reason The reason code the `refused` event carries.
 * @returns The words.
 * @example
 * refusalWord(RefusalReason.Cost);
 * // "Cannot pay"
 */
export function refusalWord(reason: number): string {
  switch (reason) {
    case RefusalReason.Dead:
      return say("bar.refused.dead");
    case RefusalReason.Stunned:
      return say("bar.refused.stunned");
    case RefusalReason.Bar:
      return say("bar.refused.bar");
    case RefusalReason.Cooldown:
      return say("bar.refused.cooldown");
    case RefusalReason.Cost:
      return say("bar.refused.cost");
    case RefusalReason.Target:
      return say("bar.refused.target");
    case RefusalReason.View:
      return say("bar.refused.view");
    case RefusalReason.Range:
      return say("bar.refused.range");
    case RefusalReason.Sight:
      return say("bar.refused.sight");
    case RefusalReason.Peace:
      return say("bar.refused.peace");
    case RefusalReason.Busy:
      return say("bar.refused.busy");
    case RefusalReason.Passage:
      return say("bar.refused.passage");
    case RefusalReason.TargetPassage:
      return say("bar.refused.targetPassage");
    case RefusalReason.Held:
      return say("bar.refused.held");
    case RefusalReason.TargetHeld:
      return say("bar.refused.targetHeld");
    default:
      return say("bar.refused.other");
  }
}

/**
 * The most whole seconds a time left on the wire can hold: `MAX_EFFECT_TICKS`, which also bounds a cooldown, rounded up.
 */
export const MAX_SECONDS = Math.ceil((MAX_EFFECT_TICKS * TICK_MS) / 1000);

/**
 * An effect's time left as the status row prints it (the design's `StatusIcon`): whole seconds with an `s` under a
 * minute, minutes and two-digit seconds from a minute on.
 *
 * @param seconds Whole seconds, 0 or more.
 * @returns The text.
 * @example
 * timeLeftText(24);
 * // "24s"
 * timeLeftText(112);
 * // "1:52"
 */
export function timeLeftText(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const rest = seconds % 60;
  return `${Math.floor(seconds / 60)}:${rest < 10 ? "0" : ""}${rest}`;
}

/**
 * Every time left the rows can print, by whole seconds from 0 to `MAX_SECONDS`.
 */
export const TIME_LEFT_TEXTS: readonly string[] = Array.from({ length: MAX_SECONDS + 1 }, (_, seconds) => timeLeftText(seconds));

/**
 * An effect's time left as its tooltip prints it (the design's "8 s left"): whole seconds with a space and `s` under a
 * minute, minutes and two-digit seconds from a minute on, as the row prints them.
 *
 * @param seconds Whole seconds, 0 or more.
 * @returns The text.
 * @example
 * tipTimeText(8);
 * // "8 s"
 */
export function tipTimeText(seconds: number): string {
  return seconds < 60 ? `${seconds} s` : timeLeftText(seconds);
}

/**
 * Every time left an effect's tooltip can print, by whole seconds from 0 to `MAX_SECONDS`.
 */
export const TIP_TIME_TEXTS: readonly string[] = Array.from({ length: MAX_SECONDS + 1 }, (_, seconds) => tipTimeText(seconds));

/**
 * Every cooldown the slots can print, by whole seconds from 0 to `MAX_SECONDS`: the seconds alone, as the design's wipe
 * shows them.
 */
export const COOLDOWN_TEXTS: readonly string[] = Array.from({ length: MAX_SECONDS + 1 }, (_, seconds) => String(seconds));

/**
 * The whole seconds a time left in ms prints as: rounded up, so a time left shows its last second until it ends, and
 * held to the tables.
 *
 * @param ms The time left in ms.
 * @returns The seconds, 1 to `MAX_SECONDS` while any time is left, 0 for none.
 */
export function secondsLeft(ms: number): number {
  if (!(ms > 0)) {
    return 0;
  }
  return Math.min(MAX_SECONDS, Math.ceil(ms / 1000));
}
